import type { FilterState, ParcelBoundary, ParcelIdentifyResult, SaleRecord } from './types';
import { isoDate } from './format';

// Bump this when the serialized cache shape or freshness rules change so old
// browser storage does not leak into a new data model.
const CACHE_KEY = 'gischarter:rutherford-sales:v4';

interface CachePayload {
  savedAt: string;
  dateFrom: string;
  dateTo: string;
  records: Array<Omit<SaleRecord, 'saleDate'> & { saleDate: string | null }>;
}

export interface CachedSales {
  savedAt: string;
  records: SaleRecord[];
}

interface ApiSaleRecord extends Omit<SaleRecord, 'saleDate'> {
  saleDate: string | null;
}

interface SalesApiResponse {
  generatedAt: string;
  freshUntil: string;
  stale: boolean;
  records: ApiSaleRecord[];
  error?: string;
}

// Reuse in-flight boundary lookups so repeated map interactions do not fan out
// extra requests for the same parcel before the first one settles.
const parcelBoundaryCache = new Map<string, Promise<ParcelBoundary | null>>();
const parcelIdentifyCache = new Map<string, Promise<ParcelIdentifyResult | null>>();
let countyBoundaryCache: Promise<ParcelBoundary | null> | null = null;

function pricePerSqft(price: number | null, buildingSize: number | null): number | null {
  if (price === null || buildingSize === null || buildingSize <= 0) return null;
  return price / buildingSize;
}

export async function fetchParcelBoundary(parcelNumber: string): Promise<ParcelBoundary | null> {
  const cacheKey = parcelNumber.trim();
  if (!cacheKey) return null;

  let cached = parcelBoundaryCache.get(cacheKey);
  if (!cached) {
    const params = new URLSearchParams({ parcelNumber: cacheKey });
    cached = fetch(`/api/parcel-boundary?${params.toString()}`)
      .then(async (response) => {
        if (!response.ok) return null;
        return (await response.json()) as ParcelBoundary | null;
      })
      .catch(() => null)
      .then((boundary) => {
        if (boundary === null) parcelBoundaryCache.delete(cacheKey);
        return boundary;
      });
    parcelBoundaryCache.set(cacheKey, cached);
  }

  return cached;
}

function coordinateCacheKey(latitude: number, longitude: number): string {
  return `${latitude.toFixed(5)},${longitude.toFixed(5)}`;
}

export async function fetchParcelAtPoint(latitude: number, longitude: number): Promise<ParcelIdentifyResult | null> {
  const cacheKey = coordinateCacheKey(latitude, longitude);
  let cached = parcelIdentifyCache.get(cacheKey);

  if (!cached) {
    const params = new URLSearchParams({
      lat: latitude.toFixed(6),
      lng: longitude.toFixed(6),
    });
    cached = fetch(`/api/parcel-identify?${params.toString()}`)
      .then(async (response) => {
        if (!response.ok) return null;
        return (await response.json()) as ParcelIdentifyResult | null;
      })
      .catch(() => null)
      .then((parcel) => {
        if (parcel === null) parcelIdentifyCache.delete(cacheKey);
        return parcel;
      });
    parcelIdentifyCache.set(cacheKey, cached);
  }

  return cached;
}

export async function fetchCountyBoundary(): Promise<ParcelBoundary | null> {
  countyBoundaryCache ??= fetch('/api/county-boundary')
    .then(async (response) => {
      if (!response.ok) return null;
      return (await response.json()) as ParcelBoundary | null;
    })
    .catch(() => null)
    .then((boundary) => {
      if (boundary === null) countyBoundaryCache = null;
      return boundary;
    });

  return countyBoundaryCache;
}

export function demoSales(): SaleRecord[] {
  const today = new Date();
  // These fixtures keep demo mode deterministic and give the UI a spread of
  // prices, locations, and parcel numbers without depending on live ArcGIS.
  const examples = [
    ['Main Street Area', -81.863, 35.367, 289000, 8, '905857'],
    ['Forest City Residential', -81.872, 35.333, 242500, 32, '1640396'],
    ['Spindale Neighborhood', -81.929, 35.358, 198000, 76, '1124501'],
    ['Rutherfordton West', -81.975, 35.374, 375000, 128, '1188202'],
    ['Lake Lure Area', -82.199, 35.431, 525000, 214, '1459303'],
    ['Ellenboro Area', -81.759, 35.334, 165000, 301, '1004108'],
  ] as const;

  return examples.map(([address, longitude, latitude, price, ageDays, parcelNumber], index) => {
    const saleDate = new Date(today.getTime() - ageDays * 86_400_000);
    const buildingSize = 1850 + index * 210;
    return {
      id: `demo-${index + 1}`,
      saleDate,
      saleDateLabel: isoDate(saleDate),
      saleDateMs: saleDate.getTime(),
      price,
      pricePerSqft: pricePerSqft(price, buildingSize),
      address,
      parcelNumber,
      buildingSize,
      acreage: Number((0.42 + index * 0.37).toFixed(2)),
      saleType: index === 2 ? 'LAND ONLY' : 'Demo sale',
      landClass: index === 2 ? 'Vacant' : 'Residential',
      latitude,
      longitude,
      propertyUrl: `https://lrcpwa.ncptscloud.com/Rutherford/parcel-detail?REID=${encodeURIComponent(parcelNumber)}`,
    };
  });
}

function saleFromApi(record: ApiSaleRecord): SaleRecord {
  return {
    ...record,
    saleDate: record.saleDate ? new Date(record.saleDate) : null,
  };
}

export async function fetchSales(filters: FilterState, onProgress: (count: number) => void): Promise<SaleRecord[]> {
  const params = new URLSearchParams();
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);

  // The API is filtered server-side, so the client only sends the active date
  // window and lets the response determine how many records were materialized.
  const response = await fetch(`/api/sales?${params.toString()}`);
  const payload = (await response.json().catch(() => null)) as SalesApiResponse | null;

  if (!response.ok) {
    throw new Error(payload?.error ?? `Sales request failed with HTTP ${response.status}`);
  }

  if (!payload || !Array.isArray(payload.records)) {
    throw new Error('Sales response was invalid.');
  }

  const records = payload.records.map(saleFromApi);
  onProgress(records.length);
  return records;
}

export function saveCache(filters: FilterState, records: SaleRecord[]): void {
  // Cache is intentionally scoped to the current date window because other
  // filters are applied client-side after the sales payload is loaded.
  const payload: CachePayload = {
    savedAt: new Date().toISOString(),
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    records: records.map((record) => ({ ...record, saleDate: record.saleDate?.toISOString() ?? null })),
  };
  localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
}

export function loadCache(filters: FilterState): CachedSales | null {
  const raw = localStorage.getItem(CACHE_KEY);
  if (!raw) return null;

  try {
    const payload = JSON.parse(raw) as CachePayload;
    // A cache hit is only valid if it matches the same server-side date
    // window; otherwise we could show stale sales for a different query.
    if (payload.dateFrom !== filters.dateFrom || payload.dateTo !== filters.dateTo) return null;

    return {
      savedAt: payload.savedAt,
      records: payload.records.map((record) => ({
        ...record,
        saleDate: record.saleDate ? new Date(record.saleDate) : null,
      })),
    };
  } catch {
    localStorage.removeItem(CACHE_KEY);
    return null;
  }
}
